import { useRef, useMemo, useEffect, useState, useCallback } from 'react'
import { Currency } from '@sequencehq/api'
import {
  GreyGrey30,
  ShadowXs,
  Spinner,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel
} from '@tanstack/react-table'
import { useAtom } from 'jotai'
import { expandedGroupsAtom, expansionModeAtom } from './groupingEngine'
import { useTableSelection } from './hooks/useTableSelection'
import { useShadowEffects } from './hooks/useShadowEffects'
import { useTableKeyboard } from './hooks/useTableKeyboard'
import type { TableRow } from './groupingEngine'
import { TableContextMenu } from './components/TableContextMenu'
import {
  TopShadowEffect,
  BottomShadowEffect,
  LeftShadowEffect,
  RightShadowEffect
} from './components/ShadowEffects'
import {
  TopHeaderRow,
  TableHeaderRow,
  VirtualDataRow,
  TableSummaryRow
} from './components/TableSections'
import type {
  RevenueWaterfallGrouping,
  RevenueWaterfallSortBy,
  WaterfallData
} from './types'
import type { RevenueWaterfallSortState } from '../../hooks/useRevenueWaterfallSorting'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

const LAYOUT_WIDTHS = {
  leftSection: 400,
  customerColumn: 200,
  totalColumn: 200,
  rightSection: 250,
  recognizedColumn: 125,
  remainingColumn: 125,
  monthColumn: 120
} as const

type Props = {
  data: WaterfallData
  asOfDate: string
  currency: Currency
  primaryGroup?: RevenueWaterfallGrouping
  sortState: RevenueWaterfallSortState
  onSort: (column: RevenueWaterfallSortBy) => void
  isFetching?: boolean
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onAddToChat?: (selectionData: {
    rowCount: number
    columnCount: number
    rowLabels: string[]
    columnLabels: string[]
    cellData: Array<{
      row: number
      col: number
      value: string
      rowLabel: string
      columnLabel: string
    }>
  }) => void
}

const ROW_HEIGHT = 32
const DEFAULT_VIEWPORT_ROW_COUNT = 15
// Balanced overscan - enough to prevent flashes without killing expand performance
const OVERSCAN_COUNT = 50 // Fixed overscan for consistent performance

const LEFT_SECTION_COLUMN_COUNT = 2

export const RevenueWaterfallTable = ({
  data,
  asOfDate,
  currency,
  primaryGroup = 'CUSTOMER',
  sortState,
  onSort,
  isFetching = false,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  onAddToChat
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [expandedGroups, setExpandedGroups] = useAtom(expandedGroupsAtom)
  const [expansionMode, setExpansionMode] = useAtom(expansionModeAtom)
  const pendingLoadRef = useRef(false)
  const [viewportRowCount, setViewportRowCount] = useState(
    DEFAULT_VIEWPORT_ROW_COUNT
  )

  const hierarchicalData = data.rows

  const expandableGroupIds = useMemo(() => {
    const ids = new Set<string>()

    const visit = (row: TableRow) => {
      if (row.children.length > 0) {
        ids.add(row.internalId)
        row.children.forEach(visit)
      }
    }

    hierarchicalData.forEach(visit)

    return Array.from(ids)
  }, [hierarchicalData])

  // Memoize expanded state to prevent TanStack Table recalculation on every render
  const expandedState = useMemo(
    () =>
      Object.fromEntries(
        Array.from(expandedGroups).map(groupId => [groupId, true])
      ),
    [expandedGroups]
  )

  const table = useReactTable({
    data: hierarchicalData,
    columns: [],
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => row.children,
    getRowId: row => row.internalId,
    state: {
      expanded: expandedState
    },
    onExpandedChange: updater => {
      setExpansionMode('custom')

      const applyExpandedMap = (expandedMap: Record<string, boolean>) => {
        setExpandedGroups(
          new Set(
            Object.keys(expandedMap).filter(key => expandedMap[key] === true)
          )
        )
      }

      if (typeof updater === 'function') {
        const currentExpanded = Object.fromEntries(
          Array.from(expandedGroups).map(groupId => [groupId, true])
        )
        const newExpanded = updater(currentExpanded)

        applyExpandedMap(newExpanded as Record<string, boolean>)
        return
      }

      if (updater && typeof updater === 'object') {
        applyExpandedMap(updater as Record<string, boolean>)
      }
    },
    enableExpanding: true,
    getRowCanExpand: row => row.subRows.length > 0
  })

  useEffect(() => {
    if (expansionMode === 'all') {
      setExpandedGroups((prev: Set<string>) => {
        if (
          prev.size === expandableGroupIds.length &&
          expandableGroupIds.every(id => prev.has(id))
        ) {
          return prev
        }

        return new Set(expandableGroupIds)
      })
    } else if (expansionMode === 'none') {
      setExpandedGroups((prev: Set<string>) => {
        if (prev.size === 0) {
          return prev
        }

        return new Set<string>()
      })
    }
  }, [expansionMode, expandableGroupIds, setExpandedGroups])

  const allRows = table.getRowModel().rows

  const shadows = useShadowEffects(scrollRef)
  const selection = useTableSelection(allRows)

  // Extract primitive values for efficient prop passing
  // before, it was an object which was causing rerenders on change
  const {
    selectionBoundaries, // { minRow, maxRow, minCol, maxCol, focusRow, focusCol, version }
    handleCellClick,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellRightClick: selectionHandleCellRightClick
  } = selection

  const flags = useFeatureFlags()
  const contextMenuEnabled = flags.useChattyRevRec && Boolean(onAddToChat)

  const handleRowContextMenu = useCallback(
    (
      row: number,
      col: number,
      event: React.MouseEvent,
      data: any,
      cellCurrency: string
    ) => {
      if (!contextMenuEnabled) {
        event.preventDefault()
        return
      }

      selectionHandleCellRightClick(row, col, event, data, cellCurrency)
    },
    [contextMenuEnabled, selectionHandleCellRightClick]
  )

  const keyboard = useTableKeyboard({
    currentPosition: selection.currentPosition,
    setCurrentPosition: selection.setCurrentPosition,
    selectedRange: selection.selectedRange,
    selectionStart: selection.selectionStart,
    setSelectedRange: selection.setSelectedRange,
    setSelectionStart: selection.setSelectionStart,
    clearSelection: selection.clearSelection,
    allRows,
    data,
    currency
  })

  useEffect(() => {
    const scrollElement = scrollRef.current

    if (!scrollElement || typeof ResizeObserver === 'undefined') {
      return
    }

    const updateViewportCount = () => {
      const nextCount = Math.ceil(scrollElement.clientHeight / ROW_HEIGHT)
      if (!Number.isFinite(nextCount) || nextCount <= 0) {
        setViewportRowCount(DEFAULT_VIEWPORT_ROW_COUNT)
        return
      }

      setViewportRowCount(prev => (prev === nextCount ? prev : nextCount))
    }

    updateViewportCount()

    const resizeObserver = new ResizeObserver(updateViewportCount)
    resizeObserver.observe(scrollElement)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const rowVirtualizer = useVirtualizer({
    count: allRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN_COUNT,
    scrollMargin: 0,
    // Disable measuring since we know the exact size
    measureElement: undefined
  })

  const virtualRows = rowVirtualizer.getVirtualItems()

  // This keeps previous rows during fast scrolling to prevent white flashes
  const previousVirtualRowsRef = useRef(virtualRows)
  const stableVirtualRows = useMemo(() => {
    if (virtualRows.length > 0) {
      previousVirtualRowsRef.current = virtualRows
      return virtualRows
    }
    // Return previous rows if current is empty (prevents flash)
    return previousVirtualRowsRef.current
  }, [virtualRows])

  useEffect(() => {
    if (!hasNextPage || !fetchNextPage || isFetchingNextPage) {
      return
    }

    if (!stableVirtualRows.length || allRows.length === 0) {
      return
    }

    const lastVirtualRow = stableVirtualRows[stableVirtualRows.length - 1]
    const loadMoreBuffer = Math.max(20, viewportRowCount * 4)

    if (
      !pendingLoadRef.current &&
      lastVirtualRow.index >= allRows.length - loadMoreBuffer
    ) {
      pendingLoadRef.current = true
      fetchNextPage()
    }
  }, [
    stableVirtualRows,
    allRows.length,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    viewportRowCount
  ])

  useEffect(() => {
    if (!isFetchingNextPage) {
      pendingLoadRef.current = false
    }
  }, [isFetchingNextPage])

  const handleAddToChat = () => {
    if (!contextMenuEnabled) {
      return
    }

    if (selection.contextMenu.selectionInfo && onAddToChat) {
      onAddToChat(selection.contextMenu.selectionInfo)
    }
  }

  // Scroll to current position when it changes (ONLY for keyboard navigation, not mouse drag)
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    //  Dont autoscroll during mouse drag selection (prevents janky behavior)
    // This was causing the table to jump when selecting with mouse and moving "down" the table
    if (selection.isDragging) return

    const { row, col } = selection.currentPosition

    // If no focused cell, do nothing
    if (row < 0 || col < 0) return

    const SUMMARY_ROW_HEIGHT = 32
    const PADDING = 8

    // Use virtualizer to scroll row into view first
    rowVirtualizer.scrollToIndex(row, { align: 'auto' })

    // Then adjust for footer in next frame (after virtualizer scroll completes)
    requestAnimationFrame(() => {
      const virtualItems = rowVirtualizer.getVirtualItems()
      const targetItem = virtualItems.find(item => item.index === row)

      if (targetItem && container) {
        const containerHeight = container.clientHeight
        const scrollTop = container.scrollTop
        const visibleHeight = containerHeight - SUMMARY_ROW_HEIGHT

        const cellTop = targetItem.start
        const cellBottom = cellTop + targetItem.size

        const visibleBottom = scrollTop + visibleHeight

        // Check if cell is hidden behind footer
        if (cellBottom > visibleBottom) {
          // Cell is hidden by footer -> scroll down a bit more
          const targetScroll = cellBottom - visibleHeight + PADDING
          container.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          })
        }
      }
    })

    const LAYOUT_WIDTHS = {
      customerColumn: 200,
      totalColumn: 200,
      monthColumn: 120,
      recognizedColumn: 125,
      remainingColumn: 125
    }

    if (
      col >= LEFT_SECTION_COLUMN_COUNT &&
      col < LEFT_SECTION_COLUMN_COUNT + data.monthlyColumns.length
    ) {
      const monthIndex = col - LEFT_SECTION_COLUMN_COUNT
      const monthsScrollableWidth = monthIndex * LAYOUT_WIDTHS.monthColumn

      const containerWidth = container.clientWidth
      const leftSectionWidth = 400
      const rightSectionWidth = 250
      const visibleWidth = containerWidth - leftSectionWidth - rightSectionWidth

      const targetScrollLeft =
        monthsScrollableWidth -
        visibleWidth / LEFT_SECTION_COLUMN_COUNT +
        LAYOUT_WIDTHS.monthColumn / LEFT_SECTION_COLUMN_COUNT

      container.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: 'smooth'
      })
    }
  }, [
    selection.currentPosition,
    selection.isDragging,
    data.monthlyColumns.length
  ])

  useEffect(() => {
    if (!selection.isDragging) return

    const container = scrollRef.current
    if (!container) return

    const EDGE_THRESHOLD = 80 // Pixels from edge to trigger scroll. If more needed, increase this
    const MAX_SCROLL_SPEED = 20
    const SCROLL_ACCELERATION = 0.3

    let animationFrameId: number | null = null
    let lastMouseY = 0

    const handleAutoScroll = () => {
      if (!selection.isDragging || !container) {
        if (animationFrameId) cancelAnimationFrame(animationFrameId)
        return
      }

      const rect = container.getBoundingClientRect()
      const mouseY = lastMouseY

      // Calculate distance from edges
      const distanceFromTop = mouseY - rect.top
      const distanceFromBottom = rect.bottom - mouseY

      let scrollSpeed = 0

      // Near top edge -> scroll up
      if (distanceFromTop < EDGE_THRESHOLD && distanceFromTop > 0) {
        const intensity = 1 - distanceFromTop / EDGE_THRESHOLD
        scrollSpeed = -intensity * MAX_SCROLL_SPEED * SCROLL_ACCELERATION
      }
      // Near bottom edge -> scroll down
      else if (distanceFromBottom < EDGE_THRESHOLD && distanceFromBottom > 0) {
        const intensity = 1 - distanceFromBottom / EDGE_THRESHOLD
        scrollSpeed = intensity * MAX_SCROLL_SPEED * SCROLL_ACCELERATION
      }

      if (scrollSpeed !== 0) {
        container.scrollTop += scrollSpeed
      }

      animationFrameId = requestAnimationFrame(handleAutoScroll)
    }

    const handleMouseMove = (e: MouseEvent) => {
      lastMouseY = e.clientY
    }

    document.addEventListener('mousemove', handleMouseMove)
    animationFrameId = requestAnimationFrame(handleAutoScroll)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      if (animationFrameId) cancelAnimationFrame(animationFrameId)
    }
  }, [selection.isDragging])

  // Clear selection when clicking outside the table container
  const { clearSelection, closeContextMenu, setCurrentPosition, contextMenu } =
    selection
  const isContextMenuOpen = contextMenu.isOpen

  useEffect(() => {
    const handleDocMouseDown = (e: MouseEvent) => {
      const containerEl = containerRef.current
      if (!containerEl) return

      const target = e.target as Node | null
      if (!target) return

      // If click is outside the table container
      if (!containerEl.contains(target)) {
        // If context menu is open, let its own outside-click handler run,
        // and rely on onClose (wired below) to also clear selection.
        if (isContextMenuOpen) return

        clearSelection()
        closeContextMenu()
        setCurrentPosition({ row: -1, col: -1 })
      }
    }

    document.addEventListener('mousedown', handleDocMouseDown)
    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown)
    }
  }, [clearSelection, closeContextMenu, setCurrentPosition, isContextMenuOpen])

  if (!data) {
    return null
  }

  return (
    <Box
      ref={containerRef}
      bg="white"
      position="relative"
      borderRadius="8px"
      w="full"
      border={`1px solid ${GreyGrey30}`}
      boxShadow={ShadowXs}
      overflow="hidden"
      maxHeight="calc(100vh - 126px)"
      display="flex"
      flexDirection="column"
      onKeyDown={keyboard.handleKeyDown}
      onClick={e => {
        // Ensure table gets focus for keyboard navigation
        if (e.currentTarget) {
          e.currentTarget.focus()
        }
      }}
      tabIndex={0}
      outline="none"
      data-testid="revrec.waterfall.table"
    >
      {/* Top header group */}
      <TopHeaderRow asOfDate={asOfDate} />

      {/* Main scrollable container */}
      <Box
        ref={scrollRef}
        flex={1}
        overflow="auto"
        position="relative"
        data-scroll-container
        style={{
          scrollBehavior: 'auto',
          willChange: 'scroll-position',
          // CSS containment for better performance
          contain: 'layout style paint',
          // Enable GPU acceleration
          transform: 'translateZ(0)',
          // Prevent layout thrashing
          backfaceVisibility: 'hidden'
        }}
      >
        <Box position="relative">
          {/* Table headers */}
          <TableHeaderRow
            data={data}
            sortState={sortState}
            onSort={onSort}
            primaryGroup={primaryGroup}
            isFetching={isFetching}
          />
          {/* Virtual scrolling container */}
          <Box
            position="relative"
            height={`${rowVirtualizer.getTotalSize()}px`}
            minHeight={allRows.length > 0 ? '32px' : '0px'}
            minWidth={`${LAYOUT_WIDTHS.leftSection + LAYOUT_WIDTHS.rightSection + data.monthlyColumns.length * LAYOUT_WIDTHS.monthColumn}px`}
            style={{
              contain: 'layout style paint',
              willChange: 'transform'
            }}
          >
            {stableVirtualRows.map(virtualRow => {
              const row = allRows[virtualRow.index]
              if (!row) return null

              const isExpanded = row.getIsExpanded()

              return (
                <VirtualDataRow
                  key={`${row.id}-${virtualRow.index}`}
                  row={row}
                  isExpanded={isExpanded}
                  virtualRow={virtualRow}
                  data={data}
                  currency={currency}
                  // Pass primitive selection props for memoization
                  selectionMinRow={selectionBoundaries.minRow}
                  selectionMaxRow={selectionBoundaries.maxRow}
                  selectionMinCol={selectionBoundaries.minCol}
                  selectionMaxCol={selectionBoundaries.maxCol}
                  focusRow={selectionBoundaries.focusRow}
                  focusCol={selectionBoundaries.focusCol}
                  selectionVersion={selectionBoundaries.version}
                  // Stable handler references
                  onCellClick={handleCellClick}
                  onCellMouseDown={handleCellMouseDown}
                  onCellMouseEnter={handleCellMouseEnter}
                  onCellRightClick={handleRowContextMenu}
                />
              )
            })}
          </Box>
          {/* Summary row */}
          <TableSummaryRow data={data} currency={currency} />
        </Box>
      </Box>

      {contextMenuEnabled ? (
        <TableContextMenu
          isOpen={selection.contextMenu.isOpen}
          position={selection.contextMenu.position}
          onClose={closeContextMenu}
          onAddToChat={handleAddToChat}
          selectionInfo={selection.contextMenu.selectionInfo}
        />
      ) : null}

      {/* Shadow effects - always rendered, visibility controlled by direct DOM manipulation */}
      <TopShadowEffect ref={shadows.topShadowRef} />
      <BottomShadowEffect ref={shadows.bottomShadowRef} />
      <LeftShadowEffect ref={shadows.leftShadowRef} />
      <RightShadowEffect ref={shadows.rightShadowRef} />

      {isFetchingNextPage ? (
        <Flex
          position="absolute"
          bottom="16px"
          right="24px"
          alignItems="center"
          gap={2}
          bg="white"
          borderRadius="md"
          px={3}
          py={1.5}
          boxShadow={ShadowXs}
          border={`1px solid ${GreyGrey30}`}
        >
          <Spinner size="sm" />
        </Flex>
      ) : null}
    </Box>
  )
}
